/**
 * @file     TheAreaCardClient.tsx
 * @readme   ../../CLAUDE.md
 * @purpose  ONE segmented "The area" card replacing the six stacked area
 *           panels. Header: title + 3-stat headline strip (demand readout,
 *           dominant home type, price trend). Segment control swaps the
 *           content region (Market / Homes / People / Supply) with a 200ms
 *           crossfade; segments whose panels have no data are dropped.
 *           Chart bodies are next/dynamic (loading fallback REQUIRED — see
 *           TheAreaCard.suspense.test.tsx) and mount only once a
 *           segment is first activated, staying mounted after. Premium
 *           gating (People / Supply) and segment layout come from
 *           panelRegistry.AREA_SEGMENTS. MarketDemandSection keeps its
 *           existing VOW-sourced data path (props are null unless the
 *           server loaded them under the VOW gate).
 *           MarketAndHistorySections, PremiumPanelGate
 */
'use client';

import * as React from 'react';
import dynamic from 'next/dynamic';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Fade from '@mui/material/Fade';
import Skeleton from '@mui/material/Skeleton';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import type { ComponentProps } from 'react';
import { PremiumPanelGate } from '@/components/auth/PremiumPanelGate';
import { PanelCard, PanelEyebrow, PanelTitle } from './PanelPrimitives';
import { microLabelSx } from '@/components/hub/hubKitStyles';
import { AREA_SEGMENTS, type AreaPanelKey, type AreaSegmentEntry } from './panelRegistry';
import { MarketDemandSection, demandReadout } from './MarketAndHistorySections';
import type { AreaMixPanelClient } from './AreaMixPanelClient';
import type { HousingStockPanelClient } from './HousingStockPanelClient';
import type { DemographicsPanelClient } from './DemographicsPanelClient';
import type { MarketStoryPanelClient } from './MarketStoryPanelClient';
import type { SupplyOutlookPanelClient } from './SupplyOutlookPanelClient';

const panelLoading = () => <Skeleton variant="rounded" height={220} />;
/** Chart chunks stay lazy: they load the first time their segment activates. */
const LAZY_PANELS = {
  areaMix: dynamic(() => import('./AreaMixPanelClient').then((m) => m.AreaMixPanelClient), { loading: panelLoading }),
  housingStock: dynamic(() => import('./HousingStockPanelClient').then((m) => m.HousingStockPanelClient), { loading: panelLoading }),
  demographics: dynamic(() => import('./DemographicsPanelClient').then((m) => m.DemographicsPanelClient), { loading: panelLoading }),
  marketStory: dynamic(() => import('./MarketStoryPanelClient').then((m) => m.MarketStoryPanelClient), { loading: panelLoading }),
  supplyOutlook: dynamic(() => import('./SupplyOutlookPanelClient').then((m) => m.SupplyOutlookPanelClient), { loading: panelLoading }),
} as const;

export interface TheAreaCardProps {
  listingKey: string;
  areaMix: ComponentProps<typeof AreaMixPanelClient> | null;
  housingStock: ComponentProps<typeof HousingStockPanelClient> | null;
  demographics: ComponentProps<typeof DemographicsPanelClient> | null;
  marketStory: ComponentProps<typeof MarketStoryPanelClient> | null;
  supplyOutlook: ComponentProps<typeof SupplyOutlookPanelClient> | null;
  marketDemand: { demandIndex: number; caption?: string } | null;
  /**
   * 'card' (default): own PanelCard with eyebrow + "The area" title (map
   * slide-up, standalone). 'bare': no card chrome and no title, for use
   * inside an already-titled surface (detail-page tab panel); the headline
   * stat strip still renders when stats exist.
   */
  variant?: 'card' | 'bare';
  /**
   * 'segmented' (default): Homes/People/Supply toggle swaps one segment at a
   * time. 'stacked': no toggle, every segment renders vertically (panels keep
   * their own titles and premium gates; charts stay next/dynamic with loading
   * fallbacks, they all mount together).
   */
  layout?: 'segmented' | 'stacked';
  /**
   * Restrict and reorder the rendered segments. When set, only the listed
   * segment keys render, in this order (each still dropped if it has no
   * data). Omit for the full AREA_SEGMENTS order (map slide-up).
   */
  segmentOrder?: readonly AreaSegmentEntry['key'][];
  analyticsSurface?: 'slug_page' | 'map_sheet';
}

interface HeadlineStat {
  label: string;
  value: string;
}

/** Year-over-year direction from the market story price series (NHPI). */
function priceTrendStat(marketStory: TheAreaCardProps['marketStory']): HeadlineStat | null {
  const mode = marketStory?.modes?.find((m) => m.id === 'nhpi');
  const data = mode?.data;
  if (!data || data.length < 2) return null;
  const last = data[data.length - 1];
  const lastDate = new Date(last.date).getTime();
  const yearAgo = lastDate - 365 * 86_400_000;
  let base = data[0];
  for (const p of data) {
    if (new Date(p.date).getTime() <= yearAgo) base = p;
    else break;
  }
  if (!base.value) return null;
  const pct = ((last.value - base.value) / base.value) * 100;
  const word = pct > 0.5 ? 'Rising' : pct < -0.5 ? 'Easing' : 'Steady';
  const signed = `${pct > 0 ? '+' : ''}${pct.toFixed(1)}%`;
  return { label: 'New-home prices', value: `${word} · ${signed} yr` };
}

function dominantHomeStat(
  areaMix: TheAreaCardProps['areaMix'],
  housingStock: TheAreaCardProps['housingStock'],
): HeadlineStat | null {
  const slices = areaMix?.mix?.slices;
  if (slices && slices.length > 0) {
    const top = slices.reduce((a, b) => (b.pct > a.pct ? b : a));
    return { label: 'Most listed', value: `${top.label} · ${Math.round(top.pct)}%` };
  }
  const buckets = housingStock?.buckets;
  if (buckets && buckets.length > 0 && housingStock) {
    const top = buckets.reduce((a, b) => (b.value > a.value ? b : a));
    const pct = housingStock.total ? Math.round((top.value / housingStock.total) * 100) : null;
    return { label: 'Most common', value: pct != null ? `${top.label} · ${pct}%` : top.label };
  }
  return null;
}

export function TheAreaCardClient(props: TheAreaCardProps) {
  const { listingKey, marketDemand, variant = 'card', layout = 'segmented' } = props;
  const reduceMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  const fadeMs = reduceMotion ? 0 : 200;

  const hasPanel: Record<AreaPanelKey, boolean> = {
    marketStory: props.marketStory != null,
    marketDemand: marketDemand != null,
    areaMix: props.areaMix != null,
    housingStock: props.housingStock != null,
    demographics: props.demographics != null,
    supplyOutlook: props.supplyOutlook != null,
  };
  const orderedSegments = props.segmentOrder
    ? props.segmentOrder
        .map((key) => AREA_SEGMENTS.find((s) => s.key === key))
        .filter((s): s is AreaSegmentEntry => s != null)
    : AREA_SEGMENTS;
  const segments = orderedSegments.filter((s) => s.panels.some((p) => hasPanel[p]));

  const [active, setActive] = React.useState<AreaSegmentEntry['key'] | null>(segments[0]?.key ?? null);
  // Lazy mount: a segment's body renders only once first selected, then stays mounted.
  const [activated, setActivated] = React.useState<ReadonlySet<string>>(
    () => new Set(segments[0] ? [segments[0].key] : []),
  );

  if (segments.length === 0) return null;

  const stats = [
    marketDemand ? { label: 'Demand', value: demandReadout(marketDemand.demandIndex) } : null,
    dominantHomeStat(props.areaMix, props.housingStock),
    priceTrendStat(props.marketStory),
  ].filter((s): s is HeadlineStat => s != null);

  const renderPanel = (key: AreaPanelKey): React.ReactNode => {
    if (key === 'marketDemand') {
      return marketDemand ? (
        <MarketDemandSection demandIndex={marketDemand.demandIndex} caption={marketDemand.caption} />
      ) : null;
    }
    const p = props[key];
    if (!p) return null;
    const Panel = LAZY_PANELS[key] as React.ComponentType<typeof p>;
    return <Panel {...p} />;
  };

  const headerBlock = (
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      sx={{ justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, gap: 2 }}
    >
      <Box>
        <PanelEyebrow>Neighbourhood insights</PanelEyebrow>
        <PanelTitle as="h2">The area</PanelTitle>
      </Box>
      {stats.length > 0 && (
        <Stack direction="row" sx={{ gap: { xs: 2, sm: 3 }, flexWrap: 'wrap' }}>
          {stats.map((s) => (
            <Box key={s.label} sx={{ minWidth: 0 }}>
              <Typography sx={{ fontSize: 11, fontWeight: 600, letterSpacing: 0.6, textTransform: 'uppercase', color: 'text.secondary' }}>
                {s.label}
              </Typography>
              <Typography sx={{ fontSize: 14, fontWeight: 600, mt: 0.25, whiteSpace: 'nowrap' }}>
                {s.value}
              </Typography>
            </Box>
          ))}
        </Stack>
      )}
    </Stack>
  );

  const gateSegment = (segment: AreaSegmentEntry, body: React.ReactNode): React.ReactNode =>
    segment.premium ? (
      <PremiumPanelGate
        panel={segment.premium.panel}
        listingKey={listingKey}
        teaserTitle={segment.premium.teaserTitle}
        teaserBody={segment.premium.teaserBody}
        treatment={segment.premium.treatment}
        analyticsSurface={props.analyticsSurface}
      >
        {body}
      </PremiumPanelGate>
    ) : (
      body
    );

  const bodyBlock = layout === 'stacked' ? (
    <Stack sx={{ gap: 8 }}>
      {segments.map((segment) => (
        <Box key={segment.key}>
          {gateSegment(
            segment,
            <Stack sx={{ gap: 8 }}>
              {segment.panels.map((key) => {
                const panel = renderPanel(key);
                return panel ? <Box key={key}>{panel}</Box> : null;
              })}
            </Stack>,
          )}
        </Box>
      ))}
    </Stack>
  ) : (
    <>
      {segments.length > 1 && (
        <ToggleButtonGroup
          exclusive
          size="small"
          value={active}
          onChange={(_, next: AreaSegmentEntry['key'] | null) => {
            if (!next) return;
            setActive(next);
            setActivated((prev) => (prev.has(next) ? prev : new Set([...prev, next])));
          }}
          sx={{
            mt: 3,
            flexWrap: 'wrap',
            '& .MuiToggleButton-root': {
              textTransform: 'none',
              fontWeight: 500,
              fontSize: 13,
              px: 2,
              py: 0.5,
            },
          }}
        >
          {segments.map((s) => (
            <ToggleButton key={s.key} value={s.key}>
              {s.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      )}

      <Box sx={{ position: 'relative', mt: 1 }}>
        {segments.map((segment) => {
          if (!activated.has(segment.key)) return null;
          const isActive = segment.key === active;
          const body = (
            <Stack sx={{ gap: 8 }}>
              {segment.panels.map((key) => {
                const panel = renderPanel(key);
                return panel ? <Box key={key}>{panel}</Box> : null;
              })}
            </Stack>
          );
          return (
            <Fade key={segment.key} in={isActive} timeout={fadeMs} unmountOnExit={false}>
              <Box sx={{ display: isActive ? 'block' : 'none' }}>
                {segment.premium ? (
                  <PremiumPanelGate
                    panel={segment.premium.panel}
                    listingKey={listingKey}
                    teaserTitle={segment.premium.teaserTitle}
                    teaserBody={segment.premium.teaserBody}
                    treatment={segment.premium.treatment}
                    analyticsSurface={props.analyticsSurface}
                  >
                    {body}
                  </PremiumPanelGate>
                ) : (
                  body
                )}
              </Box>
            </Fade>
          );
        })}
      </Box>
    </>
  );

  if (variant === 'bare') {
    return (
      <Box>
        {stats.length > 0 && (
          <Stack direction="row" sx={{ gap: { xs: 4, sm: 8 }, flexWrap: 'wrap', mb: 4 }}>
            {stats.map((s) => (
              <Box key={s.label} sx={{ minWidth: 0 }}>
                <Typography sx={microLabelSx}>{s.label}</Typography>
                <Typography sx={{ fontSize: 15, fontWeight: 500, mt: 0.5, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                  {s.value}
                </Typography>
              </Box>
            ))}
          </Stack>
        )}
        {bodyBlock}
      </Box>
    );
  }

  return (
    <PanelCard>
      {headerBlock}
      {bodyBlock}
    </PanelCard>
  );
}
